import { FileText, PaintBucket, Pencil, Scale, Settings } from 'lucide-react';
import React from 'react';

import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

import { ProofreadingRuleType } from '../../../services/types/proofreading-types';

interface RuleTypeSelectorProps {
  value: ProofreadingRuleType;
  onChange: (type: ProofreadingRuleType) => void;
}

const typeIcons = {
  [ProofreadingRuleType.GRAMMAR]: Pencil,
  [ProofreadingRuleType.LEGAL]: Scale,
  [ProofreadingRuleType.CONTENT]: FileText,
  [ProofreadingRuleType.FORMATTING]: PaintBucket,
  [ProofreadingRuleType.STYLE]: Settings,
};

const typeLabels = {
  [ProofreadingRuleType.GRAMMAR]: 'Grammar',
  [ProofreadingRuleType.LEGAL]: 'Legal',
  [ProofreadingRuleType.CONTENT]: 'Content',
  [ProofreadingRuleType.FORMATTING]: 'Formatting',
  [ProofreadingRuleType.STYLE]: 'Style',
};

const typeDescriptions = {
  [ProofreadingRuleType.GRAMMAR]:
    'Spelling, punctuation, and grammatical correctness',
  [ProofreadingRuleType.LEGAL]: 'Legal compliance and terminology',
  [ProofreadingRuleType.CONTENT]: 'Factual accuracy and completeness',
  [ProofreadingRuleType.FORMATTING]:
    'Document structure and visual presentation',
  [ProofreadingRuleType.STYLE]: 'Writing style and consistency',
};

export const RuleTypeSelector: React.FC<RuleTypeSelectorProps> = ({
  value,
  onChange,
}) => {
  const handleChange = (newValue: string) => {
    onChange(newValue as ProofreadingRuleType);
  };

  return (
    <RadioGroup
      value={value}
      onValueChange={handleChange}
      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"
    >
      {Object.values(ProofreadingRuleType).map((type) => {
        const Icon = typeIcons[type];
        return (
          <div key={type} className="flex items-start space-x-2">
            <RadioGroupItem value={type} id={`type-${type}`} className="mt-1" />
            <Label
              htmlFor={`type-${type}`}
              className="flex flex-col cursor-pointer"
            >
              <div className="flex items-center gap-2">
                <Icon className="h-4 w-4 text-gray-600" />
                <span className="font-medium">{typeLabels[type]}</span>
              </div>
              <span className="text-xs text-gray-500 mt-1">
                {typeDescriptions[type]}
              </span>
            </Label>
          </div>
        );
      })}
    </RadioGroup>
  );
};
